<template>
    <div>
        <notification-container :notifications="notifications" @dismiss="dismissNotification"></notification-container>

        <table class="table table-sm table-striped no-margin">
            <thead>
            <tr>
                <th class="col-xs-12 col-sm-4 col-md-6">
                    Item
                </th>
                <th class="col-xs-6 col-sm-4 col-md-3">
                    Price
                </th>
                <th class="col-xs-2 col-sm-4 col-md-3">Quantity</th>
            </tr>
            </thead>
            <tbody class="merchandise__options">
            <tr v-for="(sku, index) in product.skus" :key="index">
                <template v-if="sku.available_inventory > 0">
                    <td class="col-xs-12 col-sm-4 col-md-6 align-middle">
                        {{sku.label}}
                    </td>
                    <td class="col-xs-6 col-sm-4 col-md-3 align-middle">
                        {{sku.price_display}}
                    </td>
                    <td class="col-xs-2 col-sm-2 col-md-3">
                        <div class="input-group mb-0">

                            <plus-minus-control
                                    class="float-right"
                                    :min="0"
                                    :max="maxAllowedQuantity(product, sku)"
                                    v-model="sku.quantity">
                            </plus-minus-control>
                        </div>
                    </td>
                </template>
            </tr>
            </tbody>
        </table>

        <!-- Hidden only for the guest/no-user-prop standalone "Add to basket" flow (showButton=true,
             userProp=null), which redirects via a plain URL query string with no way to carry the
             buyer's choice through. Shown both when embedded in the order-details page
             (Default.vue, showButton=false) and on the standalone merchandise page for a known user
             (showButton=true, userProp set), since that case POSTs directly and can carry the choice. -->
        <div
                class="fulfilment-method-picker"
                :class="{'fulfilment-method-picker--borderless': !bordered}"
                v-if="!(showButton && userProp === null) && product.collection && product.shippable">
            <div class="fulfilment-method-picker__title">How would you like to receive this?</div>
            <div class="fulfilment-method-picker__options">
                <label
                        class="fulfilment-option"
                        :class="{'fulfilment-option--selected': fulfilmentMethod === 'collection'}">
                    <input
                            type="radio"
                            name="fulfilment_method"
                            value="collection"
                            class="fulfilment-option__input"
                            v-model="fulfilmentMethod"
                            @change="onFulfilmentMethodChange('collection')">
                    <span class="fulfilment-option__body">
                        <span class="fulfilment-option__name">Collection</span>
                        <span
                                class="fulfilment-option__detail"
                                v-if="product.collection_description"
                                v-html="product.collection_description"></span>
                    </span>
                    <span class="fulfilment-option__price">Free</span>
                </label>
                <label
                        class="fulfilment-option"
                        :class="{'fulfilment-option--selected': fulfilmentMethod === 'delivery'}">
                    <input
                            type="radio"
                            name="fulfilment_method"
                            value="delivery"
                            class="fulfilment-option__input"
                            v-model="fulfilmentMethod"
                            @change="onFulfilmentMethodChange('delivery')">
                    <span class="fulfilment-option__body">
                        <span class="fulfilment-option__name">Delivery</span>
                        <span class="fulfilment-option__detail">Posted out to your address</span>
                    </span>
                    <span class="fulfilment-option__price">{{ deliveryPriceDisplay }}</span>
                </label>
            </div>
        </div>

        <input v-if="showButton"
               type="submit"
               value="Add to basket"
               class="btn btn-ar btn-block btn-success"
               v-on:click="addButton()">

    </div>

</template>

<script>
//See Quantity.vue. Can we merge these two components?
    import PlusMinusControl from './PlusMinsControl.vue'
    import NotificationContainer from '../Common/NotificationContainer.vue'
    import notificationsMixin from '../../mixins/notifications.js'

    export default {
        name: "sku-picker",
        mixins: [notificationsMixin],
        props: {
            productProp: {
                type: Object,
                default: () => ({})
            },
            userProp: {
                type: Object,
                default: null
            },
            participantId: {
                type: String,
                default: null
            },
            showButton: {
                type: Boolean,
                default: true
            },
            theme: {
                type: String,
                default: 'bs3'
            },
            initialFulfilmentMethod: {
                type: String,
                default: null
            },
            // False where the component already sits inside a panel — the order details page
            // renders it in a no-padding box body, where its own border would sit flush against
            // that box's and read as a box inside a box.
            bordered: {
                type: Boolean,
                default: true
            }
        },
        components: {
            PlusMinusControl,
            NotificationContainer
        },
        filters: {},
        data() {
            return {
                product: {},
                orderDetails: {},
                fulfilmentMethod: null,
            }
        },
        computed: {
            // product.skus can arrive as a JSON object rather than an array — PHP's json_encode()
            // serializes a non-sequential-keyed array as an object, and the backend's
            // collection(...)->map(...) call preserves the original keys.
            allSkus() {
                if (!this.product.skus) {
                    return [];
                }

                return Array.isArray(this.product.skus) ? this.product.skus : Object.values(this.product.skus);
            },
            selectedSkus() {
                return this.allSkus.filter(sku => sku.quantity > 0);
            },
            // Estimate only — the authoritative amount is always calculated server-side
            // (ProductsTable::calculateInvoiceAmounts()) once the order is saved.
            totalDeliveryPrice() {
                return this.selectedSkus.reduce((total, sku) => {
                    let shippingPrice = parseFloat(sku.shipping_price);

                    if (!shippingPrice) {
                        return total;
                    }

                    let quantity = parseFloat(sku.quantity) || 0;

                    let shippingForSku = this.product.is_single_shipping_price_per_order
                        ? shippingPrice
                        : shippingPrice * quantity;

                    return total + shippingForSku;
                }, 0);
            },
            deliveryPriceCurrency() {
                let sku = this.selectedSkus[0] || this.allSkus[0];
                return sku ? sku.currency : null;
            },
            // Formatted with the browser's built-in Intl API rather than the money filter/js-money
            // library, to avoid depending on that library's currency lookup being correctly wired
            // up in this bundle. Falls back to a plain amount if Intl can't format the currency
            // code as given (e.g. wrong case) — visibility of the line never depends on this
            // formatting succeeding, only on totalDeliveryPrice itself.
            // What the delivery option shows before anything is picked. totalDeliveryPrice is 0
            // until a sku has a quantity, which would have read as free delivery, so fall back to
            // one unit's fee: enough for the buyer to see what delivery costs before choosing a
            // size. Once sizes are picked it becomes the real total for that selection.
            deliveryPrice() {
                if (this.selectedSkus.length > 0) {
                    return this.totalDeliveryPrice;
                }

                let sku = this.allSkus[0];
                let shippingPrice = sku ? parseFloat(sku.shipping_price) : 0;

                return shippingPrice ? shippingPrice : 0;
            },
            deliveryPriceDisplay() {
                return this.deliveryPrice > 0 ? this.formatMoney(this.deliveryPrice) : 'Free';
            }
        },
        created() {
            this.product = this.productProp;
            this.fulfilmentMethod = this.initialFulfilmentMethod;
            this.createProductDetails();
        },
        methods: {
            // Formatted with the browser's built-in Intl API rather than the money filter/js-money
            // library, to avoid depending on that library's currency lookup being correctly wired
            // up in this bundle. Falls back to a plain amount if Intl can't format the currency
            // code as given (e.g. wrong case).
            formatMoney(amount) {
                let value = amount / 100;

                if (this.deliveryPriceCurrency) {
                    try {
                        return new Intl.NumberFormat(undefined, {
                            style: 'currency',
                            currency: this.deliveryPriceCurrency.toUpperCase()
                        }).format(value);
                    } catch (e) {
                        // fall through to the plain fallback below
                    }
                }

                return (this.deliveryPriceCurrency ? this.deliveryPriceCurrency + ' ' : '') + value.toFixed(2);
            },
            onFulfilmentMethodChange(value) {
                this.orderDetails.fulfilment_method = value;
                this.$emit('fulfilment-method-change', value);
            },
            createProductDetails() {
                let object = {
                    user_id: this.userProp !== null ? this.userProp.id : null,
                    fulfilment_method: this.fulfilmentMethod
                }
                this.orderDetails = object;
            },
            addButton(index) {
                let url = window.location.origin + '/products/' + this.product.slug + '-' + this.product.id + '/order?';
                let validSelection = false; // if at least 1 item has been purchased

                // generate the url
                _.each(this.product.skus, ((sku, index) => {
                        if (sku.quantity > 0) {
                            validSelection = true;
                            url += '&skus[' + index + '][id]=' + sku.id;
                            url += '&skus[' + index + '][quantity]=' + sku.quantity;
                        }
                    })
                );

                if (validSelection) {

                    if (this.userProp !== null) {
                        axios({
                            method: 'POST',
                            url: '/api/v0/orders/bulk',
                            data: {
                                'orderDetails': this.orderDetails,
                                'product': this.product,
                                'pId': this.participantId,
                                'identifier': this.edit ? this.orderProp.identifier : '', // identifier for edit to match existing records
                            },
                        }).then((response) => {
                            // This API always responds with HTTP 200 at the transport level, even on
                            // failure — the real outcome is JSend's own status/message in the body, not
                            // the HTTP status code, so that must be checked explicitly rather than
                            // trusting any 200 response as success.
                            if (response.data && response.data.status === 'success') {
                                window.location.href = window.location.origin + '/order-summary'
                            } else {
                                this.showAlertMessage(this.extractErrorMessage(response.data), 'error', 0, 'center');
                            }
                        }).catch((error) => {
                            this.showAlertMessage(this.extractErrorMessage(error.response ? error.response.data : null), 'error', 0, 'center');
                        })

                    } else {
                        //Existing flow
                        window.location.href = url;
                    }

                } else {
                    alert('Please select at least one item');
                }
            },

            maxAllowedQuantity(product, sku) {
                let maxProductOrderQuantity = product.max_order_quantity;
                let maxSkuOrderQuantity = sku.available_inventory;

                if (maxProductOrderQuantity > 0 && maxProductOrderQuantity < maxSkuOrderQuantity) {
                    return maxProductOrderQuantity;
                } else {
                    return maxSkuOrderQuantity;
                }
            },
            /**
             * Pulls the first specific field-level error message out of a JSend error response
             * (e.g. "Choose whether you want this product collected or delivered."), falling back
             * to the response's generic top-level message, then a generic fallback.
             */
            extractErrorMessage(responseData) {
                if (!responseData) {
                    return 'Failed to place order';
                }

                let errors = responseData.data;
                if (errors) {
                    for (let field in errors) {
                        let fieldErrors = errors[field];
                        for (let rule in fieldErrors) {
                            return fieldErrors[rule];
                        }
                    }
                }

                return responseData.message || 'Failed to place order';
            }
        }
    }
</script>
<style lang="scss">
    .align-middle {
        vertical-align: middle !important;
    }

    /**
     * Delivery method chooser, laid out as stacked option rows the way a checkout usually
     * does it: name and detail on the left, price on the right, both prices visible before
     * either is picked.
     */
    /**
     * Delivery method chooser, laid out as stacked option rows the way a checkout usually
     * does it: name and detail on the left, price on the right, both prices visible before
     * either is picked.
     */
    .fulfilment-method-picker {
        margin-top: 1.25em;
        margin-bottom: 1.25em;

        &__title {
            font-weight: 600;
            font-size: 0.9em;
            margin-bottom: 0.5em;
        }

        &__options {
            border: 1px solid #ddd;
            border-radius: 4px;
            overflow: hidden;
        }

        /* Set by the bordered prop, for callers that already wrap this in a panel of their own.
           A rule above the group replaces the container so the grouping still reads. */
        &--borderless {
            margin-top: 0;
            margin-bottom: 0;
            padding: 1em;
            border-top: 1px solid #ddd;

            .fulfilment-method-picker__options {
                border: 0;
                border-radius: 0;
            }
        }
    }

    .fulfilment-option {
        display: flex;
        align-items: flex-start;
        gap: 0.75em;
        width: 100%;
        margin: 0;
        padding: 0.7em 0.5em;
        cursor: pointer;
        font-weight: 400;
        border-radius: 3px;
        transition: background 0.15s;

        & + & {
            border-top: 1px solid #eee;
        }

        &:hover {
            background: #fafafa;
        }

        &--selected {
            background: #f5faff;
        }

        &__input {
            margin: 0.25em 0 0 0;
            flex-shrink: 0;
        }

        &__body {
            display: flex;
            flex-direction: column;
            flex: 1 1 auto;
            min-width: 0;
        }

        &__name {
            font-weight: 600;
        }

        &__detail {
            font-size: 0.85em;
            color: #666;
            margin-top: 0.15em;

            p:last-child {
                margin-bottom: 0;
            }
        }

        &__price {
            flex-shrink: 0;
            font-weight: 600;
            white-space: nowrap;
        }
    }

</style>



// WEBPACK FOOTER //
// src/Assets/components/AAA_TO_REFACTOR/SkuPicker.vue